/**
 * paywallStepProof — WEB-1740: след «этот браузер сам дошёл до пейволла».
 *
 * Зачем. Переход квиз→пейволл помечает адресную строку меткой `?s=paywall`
 * (QuizScreenWizard, replaceState), чтобы возврат со Stripe и перезагрузка
 * страницы вели на экран оплаты, а не на квиз с начала (WEB-744 этап 1.5).
 * Метка лежит у покупателя на виду: скопировал адрес, переслал — и получатель
 * попадал прямо на оплату, минуя весь квиз. Ни ответов, ни лида, ни событий
 * квиза: у владельца воронки покупки висели без прохождений под ними.
 *
 * Корень: строкой из адреса, которой управляет кто угодно, доказывали факт
 * «человек прошёл квиз». Метка обязана лишь НАПОМИНАТЬ браузеру, где он
 * остановился; право пропустить квиз даёт вот этот след — его оставляет сам
 * плеер в момент настоящего перехода, и он не уезжает вместе со ссылкой.
 *
 * Хранилище трогаем только через try/catch: в приватном режиме Safari бросает
 * SecurityError даже само обращение к `window.localStorage` (грабли
 * utils/lazyWithRetry.ts). Недоступное хранилище = следа нет = посетитель
 * просто идёт по квизу с первого экрана; страница не падает.
 *
 * ЖАЛОБА ПОКУПАТЕЛЯ 18.09.2026 — У СЛЕДА ПОЯВИЛСЯ СРОК ЖИЗНИ.
 *
 * Раньше след лежал вечно, а прогресс прохождения (`quizProgressStorage.ts`) —
 * сутки. Две половины ОДНОЙ защиты с разными сроками разъезжаются молча: через
 * неделю ссылка с меткой снова открывала экран оплаты, только снимка уже не
 * было, и пейволл получал пустые ответы — пустым или дефолтным выходило всё,
 * что он считает по ответам с вариантами (выбранная цель, выбранный тариф,
 * ветвление блоков по ним). ОГОВОРКА ПО WEB-2492: ответы квиза доходят до
 * встроенного пейволла отдельным чтением (`quizAnswersBridge.tsx`) и сейчас
 * питают только правила показа блоков; подстановки их текста в тексты
 * пейволла (`{{...}}`) пейволл по ним не строит. Ответы, исключённые решением владельца 17.09
 * (почта, телефон, вес, рост, имя), в браузере не лежат вовсе, и к сроку следа
 * отношения не имеют. Теперь срок общий и ровно один
 * (`FUNNEL_BROWSER_STATE_TTL_MS`, см. докблок там же о том, почему число одно):
 * протух след — человек проходит воронку заново, как любой новый посетитель.
 *
 * ФОРМАТ ЗАПИСИ. Было `'1'` (без отметки времени), стало `{"savedAt": <мс>}`.
 * Старую запись НЕ выбрасываем: человек может прямо сейчас стоять на экране
 * оплаты. Но и полных суток она не получает — только короткое окно
 * снисхождения (`LEGACY_PAYWALL_STEP_GRACE_MS`, находка 1 свежего взгляда):
 * отметки времени у неё нет, а значит нет и уверенности, что её снимок
 * прогресса ещё жив.
 */

import { readConsentDecision } from '@/utils/analytics/euConsent';
import {
  FUNNEL_BROWSER_STATE_TTL_MS,
  LEGACY_PAYWALL_STEP_GRACE_MS,
} from './funnelBrowserStateTtl';

const KEY_PREFIX = 'w2a_quiz_paywall_step:';

/** Запись старого формата, до появления срока жизни (жалоба 18.09). */
const LEGACY_VALUE = '1';

interface StoredPaywallStepProof {
  savedAt: number;
}

/**
 * ХВОСТ ПРОХОДА 3: отметка времени ЗАМЕТНО В БУДУЩЕМ (съехали часы устройства,
 * ручная перестановка даты) давала вечно живой след — разница «сейчас минус
 * savedAt» отрицательная, срок не наступает никогда. Такую запись считаем
 * негодной и снимаем.
 *
 * Небольшой запас нужен: часы устройства и наш `Date.now()` — это одни и те же
 * часы, но между записью и чтением проходит время, а в тестах и при переводе
 * часов бывает дрожание в секунды.
 */
const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

function parseProof(raw: string): StoredPaywallStepProof | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const savedAt = (parsed as Record<string, unknown>).savedAt;
    return typeof savedAt === 'number' ? { savedAt } : null;
  } catch {
    return null;
  }
}

/**
 * ХВОСТ ПРОХОДА 3: явный отказ посетителя от хранения (`w2a_consent=denied`,
 * euConsent.ts) запрещает ПРОДЛЕВАТЬ старую запись при чтении (единственное
 * место — ветка `LEGACY_VALUE` в `hasPaywallStepProof`): право читать не даёт
 * права писать задним числом.
 *
 * WEB-2349, РЕШЕНИЕ ВЛАДЕЛЬЦА 22.09.2026. На ЗАПИСЬ следа в момент настоящего
 * перехода квиз→оплата этот запрет больше не распространяется. Он стоял в
 * `writeProof`, то есть гасил и `markPaywallStepReached`, — и покупатель,
 * нажавший «Отклонить», после оплаты возвращался с платёжной системы на первый
 * экран квиза: следа нет, экран оплаты не монтируется, ни подтверждения
 * покупки, ни ссылки в приложение. След шага оплаты — не атрибуция, а
 * служебная отметка «где человек в этой воронке»: она строго необходима для
 * услуги, которую посетитель сам запросил (вернуться после оплаты на свой
 * экран), идентификатора не несёт и наружу не уходит, поэтому согласия не
 * требует (тот же разряд, что в политике: раздел «Файлы cookie», «служебные
 * записи самого прохождения», `i18n/dictionaries/legal.ts`).
 *
 * Сбой самого чтения решения трактуется как «решения нет» (fail-open) — тот же
 * приём, что в quizProgressStorage.ts: поток покупателя ломать нельзя.
 */
function isStorageDenied(): boolean {
  try {
    return readConsentDecision() === 'denied';
  } catch {
    return false;
  }
}

/** Ключ на пару (проект, квиз): у проекта квизов много, следы не общие. */
function storageKey(projectId: string, quizSlug: string): string {
  return `${KEY_PREFIX}${projectId}:${quizSlug}`;
}

/**
 * Отметить, что ЭТОТ браузер дошёл до шага пейволла по этому квизу.
 * Вызывается из того же эффекта, что ставит метку в адрес, — но ДО выхода по
 * «метка уже стоит»: иначе человек, пришедший по чужой ссылке и честно
 * прошедший квиз, остался бы без следа, и его собственная перезагрузка
 * выкинула бы его на первый экран.
 */
export function markPaywallStepReached(
  projectId: string | undefined,
  quizSlug: string | undefined,
): void {
  if (!projectId || !quizSlug) return;
  writeProof(storageKey(projectId, quizSlug), Date.now());
}

/**
 * Единственное место записи следа — и для свежего, и для переписанного старого.
 *
 * WEB-2349: решения о cookie здесь больше не спрашиваем (почему — докблок
 * `isStorageDenied`). Запрет на ПРОДЛЕНИЕ старой записи при отказе никуда не
 * делся: он стоит выше по потоку, в ветке `LEGACY_VALUE` чтения, и там он
 * строже — такая запись не продлевается, а снимается.
 */
function writeProof(key: string, savedAt: number): void {
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ savedAt } satisfies StoredPaywallStepProof),
    );
  } catch {
    // Хранилище недоступно — записать след не вышло, просто следующая
    // загрузка начнётся с квиза. Поток покупателя ломать нельзя.
  }
}

/**
 * Снять след — «права на быструю перемотку у этого браузера больше нет».
 *
 * Зовётся страницей квиза, когда чтение прогресса сбросило завершённый проход
 * (находка 1 прохода 3 свежего взгляда): обе половины факта «где человек в
 * этой воронке» обязаны уходить вместе, иначе метка `?s=paywall` открывает
 * экран оплаты без ответов. Снятие — не запись, поэтому решение о согласии
 * здесь не спрашивается: удалять своё из чужого браузера можно всегда.
 */
export function clearPaywallStepProof(
  projectId: string | undefined,
  quizSlug: string | undefined,
): void {
  if (!projectId || !quizSlug) return;
  try {
    window.localStorage.removeItem(storageKey(projectId, quizSlug));
  } catch {
    // Хранилище недоступно — снимать нечего и незачем.
  }
}

/** Есть ли у этого браузера след прохождения до пейволла по этому квизу. */
export function hasPaywallStepProof(
  projectId: string | undefined,
  quizSlug: string | undefined,
): boolean {
  if (!projectId || !quizSlug) return false;
  try {
    const key = storageKey(projectId, quizSlug);
    const raw = window.localStorage.getItem(key);
    if (!raw) return false;
    if (raw === LEGACY_VALUE) {
      // НАХОДКА 2 ЧЕТВЁРТОГО ПРОХОДА: посетитель запретил хранение — продлить
      // такую запись нельзя (запись запрещена), а значит она осталась бы `'1'`
      // навсегда и вечно пускала бы на экран оплаты. У отказавшегося снимка
      // прогресса нет вовсе, то есть пейволл открылся бы с пустыми ответами.
      // Поэтому не продлеваем, а СНИМАЕМ: удаление — не запись, оно разрешено
      // всегда (тем же обоснованием живёт `clearPaywallStepProof`).
      if (isStorageDenied()) {
        window.localStorage.removeItem(key);
        return false;
      }
      // Запись до этой правки: отметки времени у неё нет, и выбросить её
      // нельзя — человек может прямо сейчас стоять на оплате. Но и полных
      // суток она не заслуживает: её снимок прогресса мог истечь неделю назад
      // (почему именно так — докблок LEGACY_PAYWALL_STEP_GRACE_MS). Поэтому
      // отметка ставится задним числом так, чтобы жить записи осталось ровно
      // окно снисхождения.
      writeProof(
        key,
        Date.now() -
          (FUNNEL_BROWSER_STATE_TTL_MS - LEGACY_PAYWALL_STEP_GRACE_MS),
      );
      return true;
    }
    const proof = parseProof(raw);
    const age = proof ? Date.now() - proof.savedAt : 0;
    if (!proof || age > FUNNEL_BROWSER_STATE_TTL_MS || age < -FUTURE_TOLERANCE_MS) {
      // Протухший либо битый след снимаем — тот же приём, что у прогресса
      // (quizProgressStorage.ts): не копим в браузере покупателя мусор,
      // который всё равно больше ничего не разрешает.
      window.localStorage.removeItem(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
